/**
 * @krisspy-file
 * @type page
 * @name "QuickBooking"
 * @title "Quick Booking"
 * @description "Counter booking by barcode: name, scan the items, payment, done"
 * @routes ["/bookings/quick"]
 * @layout "DashboardLayout"
 * @flowName "booking-management"
 *
 * Only for merchants with barcode scanning enabled. The booking starts now
 * and is created directly as 'active' (handed over) — or, optionally, starts
 * later (e.g. a walk-in in the evening for tomorrow morning) and is created as
 * 'open' (reserved, items allocated; Start on the booking at pickup). It ends
 * at the location's closing time on the start day (or an end the operator
 * enters) and is priced with the normal pricing rules. It reuses the wizard's building blocks: availability
 * (check_availability_enhanced), pricing, computeBookingTotals, createBooking,
 * the manual-payment ledger row, the audit log and the booking webhook.
 * See docs/BARCODE_SCANNING_SPEC.md, "Flow A".
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ScanLine,
  MapPin,
  User,
  Clock,
  Package,
  X,
  Plus,
  CreditCard,
  Check,
  Loader2,
  AlertTriangle,
  Ticket,
  Layers,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMerchant } from '../contexts/MerchantContext';
import { useToast } from '../contexts/ToastContext';
import { useBarcodeScanning, useScanHandler } from '../contexts/ScanContext';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { getLocations } from '../services/locations.service';
import { getProducts } from '../services/products.service';
import { isActiveProduct, isHiddenProduct, type ProductVisibilityFlags } from '../utils/productVisibility';
import HiddenProductBadge from '../components/HiddenProductBadge';
import { getClosingPeriodsByLocationId } from '../services/closingPeriods.service';
import {
  checkAvailability,
  createBooking,
  searchCustomers,
  type AvailableProduct,
  type CustomerResult,
} from '../services/bookings.service';
import { calculatePrice, pricingRuleColumns, type ItemPricingRuleColumns } from '../services/pricing.service';
import { validateDiscountCode, incrementDiscountCodeUsage } from '../services/discountCodes.service';
import { recordManualPayment } from '../services/paymentTransactions.service';
import { insertAuditLog } from '../services/bookingAuditLog.service';
import {
  resolveBarcode,
  findBookingsHoldingUnit,
  explainUnitUnavailable,
  unitLabel,
  type ScannedUnit,
  type UnitUnavailableReason,
} from '../services/barcode.service';
import { computeBookingTotals } from '../lib/bookingTotals';
import { formatCurrency } from '../lib/currency';
import { closingTimeOnDayOf, nextOpeningTime, type ClosingPeriodLike } from '../lib/openingHours';
import { utcIsoToDatetimeLocalValue, datetimeLocalValueToUtcIso } from '../lib/timezone';
import { BOOKING_WEBHOOK_URL, determineSendNow } from '../lib/bookingWebhook';
import ScanModal from '../components/scan/ScanModal';
import ScanInput from '../components/scan/ScanInput';
import ReturnScanModal, { type ReturnScanStart } from '../components/scan/ReturnScanModal';
import type { Database } from '../lib/database.types';
import { customFieldPlaceholder, sanitizeCustomFieldHtml, type CustomFieldConfig } from '../lib/customFields';

type Location = Database['public']['Tables']['locations']['Row'];
type InventoryType = 'individual' | 'pool' | 'unlimited';

const LOCATION_STORAGE_KEY = 'quickBooking.locationId';

/** Same mapping as the new-booking wizard (reasons from check_discount_temporal_validity). */
const DISCOUNT_REJECTION_REASON_KEYS: Record<string, string> = {
  not_found: 'discountCodes.reasonNotFound',
  missing_rental_start: 'discountCodes.reasonMissingRentalStart',
  not_yet_valid: 'discountCodes.reasonNotYetValid',
  expired: 'discountCodes.reasonExpired',
  invalid_day: 'discountCodes.reasonInvalidDay',
  outside_time_window: 'discountCodes.reasonOutsideTimeWindow',
};

/** A component slot of a bundle line. Individual slots are filled by scanning. */
interface BundleSlot {
  product_type_id: string;
  product_name: string;
  size: string | null;
  needed: number;
  inventoryType: InventoryType;
  itemIds: string[];
  itemLabels: string[];
  /** Sizes of the scanned units (the wizard stores the unit's size on component rows). */
  itemSizes: (string | null)[];
}

interface QuickLine {
  key: string;
  kind: 'unit' | 'quantity' | 'bundle';
  product_type_id: string;
  productName: string;
  size: string | null;
  quantity: number;
  inventoryType: InventoryType;
  inventoryItemId: string | null;
  unitLabel: string | null;
  pricePerUnit: number;
  ruleColumns: ItemPricingRuleColumns;
  slots?: BundleSlot[];
}

interface Message {
  text: string;
  tone: 'ok' | 'error';
}

function newKey() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Duration inputs for the pricing service (same conventions as the wizard). */
function durationFor(startIso: string, endIso: string, untilClosing: boolean) {
  const elapsed = Math.max(0, (new Date(endIso).getTime() - new Date(startIso).getTime()) / 3_600_000);
  if (untilClosing) {
    // Calendar-day mode: 1 day ending at closing time (the wizard's "1 day, 0 hours").
    return { days: 1, hours: 0, elapsed, durationHours: 24 };
  }
  const total = Math.max(1, Math.ceil(elapsed));
  return { days: Math.floor(total / 24), hours: total % 24, elapsed, durationHours: total };
}

export default function QuickBooking() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { user, profile, merchantId, locationIds, isPlatformAdmin, accessControl } = useAuth();
  const { merchant, merchantLocale } = useMerchant();
  const { showToast } = useToast();
  const { enabled: scanningEnabled, feedback } = useBarcodeScanning();
  const { formatDateTime, formatTime } = useDateFormatter();
  const timezone = merchantLocale.timezone;
  const currencyCode = merchant?.currency_code || 'EUR';

  // Location
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsStatus, setLocationsStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [locationsError, setLocationsError] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string>('');
  const location = locations.find((l) => l.id === locationId) || null;
  const [closingPeriods, setClosingPeriods] = useState<ClosingPeriodLike[]>([]);
  const [closingPeriodsFor, setClosingPeriodsFor] = useState<string>(''); // location they were loaded for

  // Start: "now" (taken at submit; availability is checked from load time) or a
  // later start the operator picks (defaults to the next opening time).
  const [startMode, setStartMode] = useState<'now' | 'later'>('now');
  const [startValue, setStartValue] = useState(''); // datetime-local, merchant wall clock
  const startLaterIso = startMode === 'later' && startValue ? datetimeLocalValueToUtcIso(startValue, timezone) : '';
  const startValid = startMode === 'now' || (!!startLaterIso && new Date(startLaterIso).getTime() > Date.now());
  /** The booking's start: now, or the chosen later start. */
  const effectiveStartIso = () => (startMode === 'later' ? startLaterIso : new Date().toISOString());

  // End
  const [defaultEndIso, setDefaultEndIso] = useState<string | null>(null);
  const [endValue, setEndValue] = useState(''); // datetime-local, merchant wall clock
  const endIso = endValue ? datetimeLocalValueToUtcIso(endValue, timezone) : '';
  const untilClosing = !!defaultEndIso && endIso === defaultEndIso;

  // Availability for [windowStart, endIso]
  const [availability, setAvailability] = useState<AvailableProduct[]>([]);
  const [windowStart, setWindowStart] = useState<string>('');
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // Customer
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState('');
  const [customerResults, setCustomerResults] = useState<CustomerResult[]>([]);
  const [customerSearchActive, setCustomerSearchActive] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);

  // Cart
  const [lines, setLines] = useState<QuickLine[]>([]);
  const [message, setMessage] = useState<Message | null>(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [activeUnitHit, setActiveUnitHit] = useState<{ orderId: string; start: ReturnScanStart } | null>(null);
  const [returnStart, setReturnStart] = useState<ReturnScanStart | null>(null);
  const [showBundlePicker, setShowBundlePicker] = useState(false);
  // active/hidden per product type; the availability RPC doesn't return them
  const [productFlags, setProductFlags] = useState<Map<string, ProductVisibilityFlags>>(new Map());

  // Payment, discount, custom fields
  const [paymentMethod, setPaymentMethod] = useState<'paid' | 'link' | 'later'>('later');
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState<{
    valid: boolean;
    discount: number;
    error: string | null;
    reason?: string;
    discountCodeId?: string;
  } | null>(null);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; orderId: string; text: string } | null>(null);

  const customFields: CustomFieldConfig[] = useMemo(
    () => (Array.isArray((location as any)?.custom_fields) ? (location as any).custom_fields : []),
    [location]
  );

  // ── Load locations ──
  useEffect(() => {
    if (!scanningEnabled) return;
    (async () => {
      setLocationsStatus('loading');
      const { data, error } = await getLocations({ merchantId, locationIds, isPlatformAdmin }, { active: true });
      if (error) {
        setLocations([]);
        setLocationsError(error);
        setLocationsStatus('error');
        return;
      }
      // Only the ACTIVE merchant's locations: platform admins otherwise get
      // every merchant's locations from getLocations.
      const list = ((data || []) as Location[]).filter((l) => l.merchant_id === merchantId);
      setLocations(list);
      setLocationsError(null);
      setLocationsStatus('ready');
      let saved: string | null = null;
      try { saved = localStorage.getItem(LOCATION_STORAGE_KEY); } catch { /* unavailable */ }
      const initial = list.find((l) => l.id === saved)?.id ?? list[0]?.id ?? '';
      setLocationId(initial);
    })();
  }, [scanningEnabled, merchantId, locationIds, isPlatformAdmin]);

  // ── Load product flags (once per merchant, independent of the time window) ──
  useEffect(() => {
    if (!scanningEnabled || !merchantId) return;
    (async () => {
      const { data } = await getProducts({ merchantId, locationIds: null, isPlatformAdmin }, {});
      setProductFlags(new Map((data || []).map((pt: any) => [pt.id, { active: pt.active, hidden: pt.hidden }])));
    })();
  }, [scanningEnabled, merchantId, isPlatformAdmin]);

  useEffect(() => {
    if (!merchant) return;
    setLanguage((prev) => prev || merchant.default_language || 'en');
  }, [merchant]);

  // ── Location change: closing periods, default end ──
  useEffect(() => {
    if (!locationId) return;
    try { localStorage.setItem(LOCATION_STORAGE_KEY, locationId); } catch { /* unavailable */ }
    setLines([]);
    setMessage(null);
    setCustomFieldValues({});
    (async () => {
      const { data } = await getClosingPeriodsByLocationId(locationId);
      setClosingPeriods((data || []) as ClosingPeriodLike[]);
      setClosingPeriodsFor(locationId);
    })();
  }, [locationId]);

  const resetEndToDefault = () => {
    // Closing time on the start day: today for "now", the chosen day for a later start.
    const at = startMode === 'later' ? (startLaterIso ? new Date(startLaterIso) : null) : new Date();
    const close = location && at ? closingTimeOnDayOf(location.opening_hours, closingPeriods, timezone, at) : null;
    const iso = close ? close.toISOString() : null;
    setDefaultEndIso(iso);
    setEndValue(iso ? utcIsoToDatetimeLocalValue(iso, timezone) : '');
  };

  useEffect(() => {
    resetEndToDefault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.id, closingPeriods, timezone, startMode, startLaterIso]);

  /** Switch between "now" and a later start; a later start defaults to the next opening time. */
  const chooseStartMode = (mode: 'now' | 'later') => {
    setStartMode(mode);
    if (mode === 'later' && !startValue) {
      const next = location ? nextOpeningTime(location.opening_hours, closingPeriods, timezone) : null;
      setStartValue(next ? utcIsoToDatetimeLocalValue(next.toISOString(), timezone) : '');
    }
  };

  // ── Availability for start → end ──
  const endValid =
    startValid &&
    !!endIso &&
    new Date(endIso).getTime() > (startMode === 'later' ? new Date(startLaterIso).getTime() : Date.now());

  const loadAvailability = async () => {
    if (!locationId || !endValid) {
      setAvailability([]);
      return;
    }
    setLoadingAvailability(true);
    const start = effectiveStartIso();
    const { data, error } = await checkAvailability({
      locationId,
      startDate: start,
      endDate: endIso,
      skipTimeGates: true, // admin override, as in the wizard
    });
    setLoadingAvailability(false);
    if (error) {
      setMessage({ text: error, tone: 'error' });
      return;
    }
    setWindowStart(start);
    setAvailability(data || []);
  };

  useEffect(() => {
    loadAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, endIso, startMode, startLaterIso]);

  // ── Re-price the cart when the start or end changes ──
  const priceFor = async (productTypeId: string, size: string | null) => {
    const start = effectiveStartIso();
    const d = durationFor(start, endIso, untilClosing);
    return calculatePrice(productTypeId, locationId, d.days, d.hours, size, 'admin', d.elapsed);
  };

  useEffect(() => {
    if (!endValid || lines.length === 0) return;
    let cancelled = false;
    (async () => {
      const priced = new Map<string, { pricePerUnit: number; ruleColumns: ItemPricingRuleColumns }>();
      await Promise.all(
        lines.map(async (line) => {
          const { data } = await priceFor(line.product_type_id, line.kind === 'bundle' ? null : line.size);
          if (data) priced.set(line.key, { pricePerUnit: data.price, ruleColumns: pricingRuleColumns(data.rule) });
        })
      );
      // Merge by key: lines scanned or removed while pricing ran stay as they are.
      if (!cancelled) setLines((prev) => prev.map((l) => (priced.has(l.key) ? { ...l, ...priced.get(l.key)! } : l)));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endIso, startMode, startLaterIso]);

  // ── Customer search (same RPC as the wizard) ──
  useEffect(() => {
    const query = `${firstName} ${lastName}`.trim();
    if (!customerSearchActive || query.length < 2 || !merchantId) {
      setCustomerResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      const { data } = await searchCustomers(merchantId, query, 8);
      setCustomerResults(data || []);
    }, 300);
    return () => clearTimeout(timeout);
  }, [firstName, lastName, customerSearchActive, merchantId]);

  const selectCustomer = (c: CustomerResult) => {
    setFirstName(c.customer_firstname || '');
    setLastName(c.customer_lastname || '');
    setEmail(c.customer_email || '');
    setPhone(c.customer_phone || '');
    if (c.language) setLanguage(c.language);
    setCustomerSearchActive(false);
    setCustomerResults([]);
  };

  // ── Cart helpers ──
  const unitInCart = (unitId: string) =>
    lines.some((l) => l.inventoryItemId === unitId || l.slots?.some((s) => s.itemIds.includes(unitId)));

  const poolConsumed = (productTypeId: string) =>
    lines.reduce((sum, l) => {
      if (l.kind === 'quantity' && l.product_type_id === productTypeId && l.inventoryType === 'pool') return sum + l.quantity;
      const slots = (l.slots || []).filter((s) => s.product_type_id === productTypeId && s.inventoryType === 'pool');
      return sum + slots.reduce((s2, s) => s2 + s.needed, 0);
    }, 0);

  const poolCapacity = (productTypeId: string) => {
    const rows = availability.filter((r) => r.product_type_id === productTypeId && r.inventory_type === 'pool');
    return rows.length ? Math.max(0, ...rows.map((r) => r.pool_quantity || 0)) : 0;
  };

  const removeUnitFromCart = (unitId: string) => {
    setLines((prev) =>
      prev
        .filter((l) => l.inventoryItemId !== unitId)
        .map((l) =>
          l.slots
            ? {
                ...l,
                slots: l.slots.map((s) => {
                  const idx = s.itemIds.indexOf(unitId);
                  if (idx === -1) return s;
                  return {
                    ...s,
                    itemIds: s.itemIds.filter((_, i) => i !== idx),
                    itemLabels: s.itemLabels.filter((_, i) => i !== idx),
                    itemSizes: s.itemSizes.filter((_, i) => i !== idx),
                  };
                }),
              }
            : l
        )
    );
  };

  const reasonText = (reason: UnitUnavailableReason, unit: ScannedUnit) => {
    const item = unitLabel(unit);
    switch (reason.reason) {
      case 'inactive':
        return t('scan.reasonInactive', { item });
      case 'blocked':
        return t('scan.reasonBlocked', { item, until: formatDateTime(reason.until), note: reason.note || '' });
      case 'booked':
        return t('scan.reasonBooked', { item, orderId: reason.orderId, from: formatDateTime(reason.from) });
      default:
        return t('scan.reasonNotOffered', { item });
    }
  };

  // ── Scanning ──
  const handleUnitScan = async (unit: ScannedUnit) => {
    const label = unitLabel(unit);

    if (unitInCart(unit.id)) {
      if (window.confirm(t('quickBooking.removeConfirm', { item: `${unit.product_name} ${label}` }))) {
        removeUnitFromCart(unit.id);
      }
      return;
    }

    const { data: holding } = await findBookingsHoldingUnit(accessControl, unit.id);
    const active = holding.find((b) => b.booking_state === 'active');
    if (active) {
      feedback('error');
      setActiveUnitHit({ orderId: active.order_id, start: { bookingId: active.id, unitId: unit.id } });
      setMessage({ text: t('quickBooking.unitRentedOut', { item: label, orderId: active.order_id }), tone: 'error' });
      return;
    }

    // Bundle slot first: a unit that fits an open slot fills it instead of becoming its own line.
    for (const line of lines) {
      if (line.kind !== 'bundle' || !line.slots) continue;
      const slotIdx = line.slots.findIndex(
        (s) =>
          s.inventoryType === 'individual' &&
          s.itemIds.length < s.needed &&
          availability.some(
            (r) => r.item_id === unit.id && r.product_type_id === s.product_type_id && (!s.size || r.size === s.size)
          )
      );
      if (slotIdx !== -1) {
        setLines((prev) =>
          prev.map((l) =>
            l.key !== line.key
              ? l
              : {
                  ...l,
                  slots: l.slots!.map((s, i) =>
                    i === slotIdx
                      ? { ...s, itemIds: [...s.itemIds, unit.id], itemLabels: [...s.itemLabels, label], itemSizes: [...s.itemSizes, unit.size] }
                      : s
                  ),
                }
          )
        );
        feedback('ok');
        setMessage({ text: t('quickBooking.slotFilled', { item: label, bundle: line.productName }), tone: 'ok' });
        return;
      }
    }

    // A unit can serve several product types (alt/additional); only active ones count
    const unitRows = availability.filter((r) => r.item_id === unit.id && r.inventory_type === 'individual');
    const rows = unitRows.filter((r) => isActiveProduct(productFlags.get(r.product_type_id)));
    if (
      (unitRows.length > 0 && rows.length === 0) ||
      (unitRows.length === 0 && !isActiveProduct(productFlags.get(unit.product_type_id)))
    ) {
      feedback('error');
      setMessage({ text: reasonText({ reason: 'inactive' }, unit), tone: 'error' });
      return;
    }
    let row: AvailableProduct | undefined = rows.find((r) => r.product_type_id === unit.product_type_id) ?? rows[0];
    if (!row) {
      const start = windowStart || new Date().toISOString();
      const reason = await explainUnitUnavailable(unit, start, endIso);
      if (reason.reason === 'not_offered') {
        // Products with max_concurrent_rentals only list the first N free units,
        // so a free unit further down the allocation order is missing from the
        // list although the cap still has room. Accept it when the product is
        // offered here and the cart hasn't used up the remaining slots.
        const { data: typeRows } = await checkAvailability({
          locationId,
          startDate: start,
          endDate: endIso,
          productTypeId: unit.product_type_id,
          skipTimeGates: true,
        });
        const offered = (typeRows || []).filter((r) => r.inventory_type === 'individual' && r.item_id);
        const inCart = lines.filter((l) => l.kind === 'unit' && l.product_type_id === unit.product_type_id).length;
        if (offered.length > inCart) {
          row = { ...offered[0], item_id: unit.id, item_number: unit.item_number, size: unit.size };
        } else if (offered.length > 0) {
          feedback('error');
          setMessage({ text: t('quickBooking.noneLeft', { product: offered[0].product_name }), tone: 'error' });
          return;
        }
      }
      if (!row) {
        feedback('error');
        setMessage({ text: reasonText(reason, unit), tone: 'error' });
        return;
      }
    }

    const { data: pricing, error: pricingError } = await priceFor(row.product_type_id, row.size);
    if (pricingError || !pricing) {
      feedback('error');
      setMessage({ text: t('quickBooking.noPrice', { item: row.product_name, error: pricingError || '' }), tone: 'error' });
      return;
    }

    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        kind: 'unit',
        product_type_id: row.product_type_id,
        productName: row.product_name,
        size: row.size,
        quantity: 1,
        inventoryType: 'individual',
        inventoryItemId: unit.id,
        unitLabel: label,
        pricePerUnit: pricing.price,
        ruleColumns: pricingRuleColumns(pricing.rule),
      },
    ]);
    feedback('ok');
    setMessage({ text: t('quickBooking.added', { item: `${row.product_name} ${label}` }), tone: 'ok' });
  };

  const handleProductScan = async (productTypeId: string, productName: string) => {
    const rows = availability.filter((r) => r.product_type_id === productTypeId && r.inventory_type !== 'individual');
    if (rows.length === 0 || !isActiveProduct(productFlags.get(productTypeId))) {
      feedback('error');
      setMessage({ text: t('quickBooking.productNotHere', { product: productName }), tone: 'error' });
      return;
    }
    const inventoryType = rows[0].inventory_type as InventoryType;
    if (inventoryType === 'pool' && poolConsumed(productTypeId) >= poolCapacity(productTypeId)) {
      feedback('error');
      setMessage({ text: t('quickBooking.noneLeft', { product: productName }), tone: 'error' });
      return;
    }

    const existing = lines.find((l) => l.kind === 'quantity' && l.product_type_id === productTypeId);
    if (existing) {
      setLines((prev) => prev.map((l) => (l.key === existing.key ? { ...l, quantity: l.quantity + 1 } : l)));
      feedback('ok');
      setMessage({ text: t('quickBooking.added', { item: productName }), tone: 'ok' });
      return;
    }

    const { data: pricing, error: pricingError } = await priceFor(productTypeId, rows[0].size);
    if (pricingError || !pricing) {
      feedback('error');
      setMessage({ text: t('quickBooking.noPrice', { item: productName, error: pricingError || '' }), tone: 'error' });
      return;
    }
    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        kind: 'quantity',
        product_type_id: productTypeId,
        productName: rows[0].product_name || productName,
        size: rows[0].size,
        quantity: 1,
        inventoryType,
        inventoryItemId: null,
        unitLabel: null,
        pricePerUnit: pricing.price,
        ruleColumns: pricingRuleColumns(pricing.rule),
      },
    ]);
    feedback('ok');
    setMessage({ text: t('quickBooking.added', { item: productName }), tone: 'ok' });
  };

  const handleScan = async (code: string) => {
    if (scanBusy) return;
    setActiveUnitHit(null);
    setCreated(null);
    // Not ready yet: a scan now would be checked against no or stale data.
    if (locationsStatus === 'loading' || loadingAvailability) {
      feedback('error');
      setMessage({ text: t('quickBooking.stillLoading'), tone: 'error' });
      return;
    }
    if (!location) {
      feedback('error');
      setMessage({ text: t(locations.length === 0 ? 'quickBooking.noLocations' : 'quickBooking.chooseLocationFirst'), tone: 'error' });
      return;
    }
    if (!startValid) {
      feedback('error');
      setMessage({ text: t('quickBooking.setStartFirst'), tone: 'error' });
      return;
    }
    if (!endValid) {
      feedback('error');
      setMessage({ text: t('quickBooking.setEndFirst'), tone: 'error' });
      return;
    }
    setScanBusy(true);
    try {
      const { data: res, error } = await resolveBarcode(code, merchantId);
      if (error || !res) {
        feedback('error');
        setMessage({ text: t('scan.lookupFailed', { error: error || '' }), tone: 'error' });
        return;
      }
      if (res.kind === 'unknown') {
        feedback('error');
        setMessage({ text: t('scan.unknownBarcode', { code }), tone: 'error' });
        return;
      }
      if (res.kind === 'unit') await handleUnitScan(res.unit);
      else await handleProductScan(res.product.id, res.product.product_name);
    } finally {
      setScanBusy(false);
    }
  };

  useScanHandler(handleScan, scanningEnabled && !returnStart);

  // A scan handed over from the "no booking found" dialog.
  const handedOver = useRef<number | null>(null);
  useEffect(() => {
    const state = routerLocation.state as { scanCode?: string; at?: number } | null;
    if (
      !state?.scanCode || handedOver.current === state.at || loadingAvailability || !location ||
      closingPeriodsFor !== location.id || availability.length === 0
    ) return;
    handedOver.current = state.at ?? Date.now();
    handleScan(state.scanCode);
    // Drop it from history so a page refresh doesn't add the item again.
    navigate(routerLocation.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.state, loadingAvailability, location?.id, closingPeriodsFor, availability.length]);

  // ── Bundles ──
  const bundleProducts = useMemo(() => {
    const byType = new Map<string, AvailableProduct>();
    for (const row of availability) {
      if (!row.is_bundle || !row.bundle_components || byType.has(row.product_type_id)) continue;
      if (!isActiveProduct(productFlags.get(row.product_type_id))) continue;
      byType.set(row.product_type_id, row);
    }
    return [...byType.values()];
  }, [availability, productFlags]);

  const addBundle = async (bundle: AvailableProduct) => {
    setShowBundlePicker(false);
    const components = (bundle.bundle_components || []) as Array<{ product_type_id: string; quantity?: number; size?: string | null }>;
    const slots: BundleSlot[] = [];
    // Pool units reserved by earlier components of THIS bundle, on top of the cart.
    const reservedHere = new Map<string, number>();
    for (const comp of components) {
      const rows = availability.filter((r) => r.product_type_id === comp.product_type_id);
      const needed = comp.quantity || 1;
      if (rows.length === 0) {
        setMessage({ text: t('quickBooking.bundleComponentUnavailable', { bundle: bundle.product_name }), tone: 'error' });
        return;
      }
      const inventoryType = rows[0].inventory_type as InventoryType;
      if (inventoryType === 'pool') {
        const reserved = poolConsumed(comp.product_type_id) + (reservedHere.get(comp.product_type_id) || 0);
        if (poolCapacity(comp.product_type_id) - reserved < needed) {
          setMessage({ text: t('quickBooking.noneLeft', { product: rows[0].product_name }), tone: 'error' });
          return;
        }
        reservedHere.set(comp.product_type_id, (reservedHere.get(comp.product_type_id) || 0) + needed);
      }
      slots.push({
        product_type_id: comp.product_type_id,
        product_name: rows[0].product_name,
        size: comp.size || null,
        needed,
        inventoryType,
        itemIds: [],
        itemLabels: [],
        itemSizes: [],
      });
    }

    const { data: pricing, error: pricingError } = await priceFor(bundle.product_type_id, null);
    if (pricingError || !pricing) {
      setMessage({ text: t('quickBooking.noPrice', { item: bundle.product_name, error: pricingError || '' }), tone: 'error' });
      return;
    }
    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        kind: 'bundle',
        product_type_id: bundle.product_type_id,
        productName: bundle.product_name,
        size: null,
        quantity: 1,
        inventoryType: bundle.inventory_type,
        inventoryItemId: null,
        unitLabel: null,
        pricePerUnit: pricing.price,
        ruleColumns: pricingRuleColumns(pricing.rule),
        slots,
      },
    ]);
    setMessage({ text: t('quickBooking.bundleAdded', { bundle: bundle.product_name }), tone: 'ok' });
  };

  // ── Totals ──
  const itemsTotal = lines.reduce((sum, l) => sum + l.pricePerUnit * l.quantity, 0);
  const discountAmount = discountValidation?.valid ? discountValidation.discount : 0;
  const taxRate = location?.tax_rate || 0;
  const taxIncluded = location?.tax_included ?? true;
  const totals = computeBookingTotals({
    subtotal: itemsTotal,
    deliveryPrice: 0,
    totalDiscount: discountAmount,
    taxRate,
    taxIncluded,
  });
  // A discount code that covers the whole booking leaves nothing to collect, so
  // the booking is paid regardless of which payment option was selected.
  const isFullyDiscounted = discountAmount > 0 && totals.totalAmount <= 0;
  const effectivePaymentMethod: 'paid' | 'link' | 'later' = isFullyDiscounted ? 'paid' : paymentMethod;

  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !location) {
      setDiscountValidation(null);
      return;
    }
    const result = await validateDiscountCode({
      code: discountCode,
      merchantId: location.merchant_id,
      orderAmount: itemsTotal,
      productTypeIds: lines.map((l) => l.product_type_id),
      rentalStart: new Date(),
    });
    setDiscountValidation(result as any);
  };

  // Payment link needs an email to send it to.
  useEffect(() => {
    if (paymentMethod === 'link' && !email.trim()) setPaymentMethod('later');
  }, [email, paymentMethod]);

  // ── Submit ──
  const openSlots = lines.flatMap((l) =>
    (l.slots || []).filter((s) => s.inventoryType === 'individual' && s.itemIds.length < s.needed).map((s) => ({ line: l, slot: s }))
  );

  const validate = (): string | null => {
    if (!location) return t('quickBooking.chooseLocationFirst');
    if (!firstName.trim() || !lastName.trim()) return t('quickBooking.nameRequired');
    if (!startValid) return t('quickBooking.setStartFirst');
    if (!endValid) return t('quickBooking.setEndFirst');
    if (lines.length === 0) return t('quickBooking.noItems');
    if (openSlots.length > 0) return t('quickBooking.bundleIncomplete');
    if (effectivePaymentMethod === 'link' && !email.trim()) return t('quickBooking.linkNeedsEmail');
    const missing = customFields.find((f) => f.required && !customFieldValues[f.key]?.trim());
    if (missing) return t('quickBooking.customFieldRequired', { field: missing.label });
    return null;
  };

  const resetForNextCustomer = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setLanguage(merchant?.default_language || 'en');
    setLines([]);
    setPaymentMethod('later');
    setDiscountCode('');
    setDiscountValidation(null);
    setShowDiscount(false);
    setCustomFieldValues({});
    setActiveUnitHit(null);
    // Back to "start now"; the end follows via the start effect.
    setStartMode('now');
    setStartValue('');
    if (startMode === 'now') resetEndToDefault();
    firstNameRef.current?.focus();
  };

  const handleSubmit = async () => {
    const problem = validate();
    if (problem) {
      setFormError(problem);
      return;
    }
    if (!location) return;
    setFormError(null);
    setSubmitting(true);

    const start = effectiveStartIso();
    const end = endIso;
    // Start now = handed over (active). Later start = reserved (open); the
    // operator presses Start on the booking when the customer takes the items.
    const bookingState = startMode === 'later' ? 'open' : 'active';
    if (new Date(end).getTime() <= new Date(start).getTime()) {
      setFormError(t('newBooking.errorEndBeforeStart'));
      setSubmitting(false);
      return;
    }

    const duration = durationFor(start, end, untilClosing);
    const paymentState = effectivePaymentMethod === 'paid' ? 'paid' : effectivePaymentMethod === 'link' ? 'open' : 'open_pay_later';
    const customValues = Object.keys(customFieldValues).some((k) => customFieldValues[k]?.trim()) ? customFieldValues : null;

    const bookingData = {
      merchant_id: location.merchant_id,
      location_id: location.id,
      start_date: start,
      end_date: end,
      customer_firstname: firstName.trim(),
      customer_lastname: lastName.trim(),
      customer_email: email.trim(),
      customer_phone: phone.trim(),
      booking_state: bookingState,
      payment_state: paymentState,
      amount_paid: effectivePaymentMethod === 'paid' ? totals.totalAmount : 0,
      subtotal: totals.subtotal,
      total_amount: totals.totalAmount,
      tax_rate: taxRate || null,
      tax_included: taxIncluded,
      total_tax: totals.totalTax || null,
      total_discount: discountAmount,
      discount_code_id: discountValidation?.discountCodeId || null,
      discount_code_used: discountValidation?.valid ? discountCode : null,
      currency: currencyCode,
      language: language || merchant?.default_language || 'en',
      sales_channel: 'admin',
      marketing_consent: false,
      custom_field_values: customValues,
    };

    const items = lines.map((l) => ({
      product_type_id: l.product_type_id,
      productName: l.productName,
      size: l.size,
      quantity: l.quantity,
      pricePerUnit: l.pricePerUnit,
      durationHours: duration.durationHours,
      inventoryItemId: l.inventoryItemId,
      inventoryType: l.inventoryType,
      ruleColumns: l.ruleColumns,
      ...(l.kind === 'bundle'
        ? {
            isBundle: true,
            bundleComponents: (l.slots || []).map((s) => ({
              product_type_id: s.product_type_id,
              product_name: s.product_name,
              quantity: s.needed,
              size: s.size || s.itemSizes.find((z) => z) || null,
              inventoryItemIds: s.itemIds,
              inventoryType: s.inventoryType,
            })),
          }
        : {}),
    }));

    const { data, error } = await createBooking(bookingData as any, items);
    if (error || !data) {
      setSubmitting(false);
      feedback('error');
      setFormError(error || t('newBooking.errorInvalidBooking'));
      // Something may have been booked meanwhile; refresh what's available.
      loadAvailability();
      return;
    }

    if (effectivePaymentMethod === 'paid' && totals.totalAmount > 0) {
      const { error: ledgerError } = await recordManualPayment({
        bookingId: data.id,
        amount: totals.totalAmount,
        channel: 'manual',
        notes: 'Marked as paid at booking creation (admin, quick booking)',
        userId: user?.id,
        skipBookingUpdate: true,
      });
      if (ledgerError) console.error('Failed to record payment transaction for quick booking:', ledgerError);
    }

    insertAuditLog({
      bookingId: data.id,
      merchantId: location.merchant_id,
      locationId: location.id,
      changeType: 'created',
      changeDetails: {
        booking_state: bookingState,
        payment_state: paymentState,
        sales_channel: 'admin',
        source: 'barcode_scan',
        flow: 'quick_booking',
      },
      userId: user?.id,
      userEmail: profile?.full_name || user?.email || null,
      triggeredBy: 'admin_panel',
    });

    if (discountValidation?.valid && discountValidation.discountCodeId && discountAmount > 0) {
      await incrementDiscountCodeUsage(discountValidation.discountCodeId, discountAmount);
    }

    // Same webhook as every admin-created booking. Make.com skips email / SMS /
    // WhatsApp when the corresponding contact field is empty.
    const loc = location as any;
    fetch(BOOKING_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        booking_id: data.id,
        end_date: end,
        send_now: determineSendNow(loc, start),
        booking_state: bookingState,
        payment_state: paymentState,
        payment_flow: effectivePaymentMethod === 'later' ? 'pay_later' : effectivePaymentMethod,
        language: bookingData.language,
        self_service_enabled: loc.self_service_enabled ?? false,
        deliveries: loc.deliveries ?? false,
        custom_fields: customValues,
      }),
    }).catch((err) => console.error('Failed to trigger booking webhook:', err));

    feedback('ok');
    const createdText = bookingState === 'open'
      ? t('quickBooking.createdReserved', { orderId: data.order_id, start: formatDateTime(start) })
      : t('quickBooking.created', { orderId: data.order_id });
    showToast(createdText, 'success');
    setCreated({ id: data.id, orderId: data.order_id, text: createdText });
    setSubmitting(false);
    resetForNextCustomer();
    loadAvailability();
  };

  // ── Render ──
  if (!scanningEnabled) {
    return (
      <div className="card p-lg max-w-xl" data-testid="quick-booking-unavailable">
        <h1 className="text-xl font-bold text-primary mb-sm">{t('quickBooking.title')}</h1>
        <p className="text-sm text-secondary">{t('quickBooking.notEnabled')}</p>
      </div>
    );
  }

  const discountReason = discountValidation && !discountValidation.valid
    ? (discountValidation.reason && DISCOUNT_REJECTION_REASON_KEYS[discountValidation.reason]
        ? t(DISCOUNT_REJECTION_REASON_KEYS[discountValidation.reason])
        : discountValidation.error)
    : null;

  return (
    <div className="space-y-lg animate-fade-in max-w-4xl" data-testid="quick-booking">
      <div className="flex flex-wrap items-center justify-between gap-md">
        <div>
          <h1 className="text-3xl font-bold text-primary">{t('quickBooking.title')}</h1>
          <p className="text-sm text-secondary">{t('quickBooking.subtitle')}</p>
        </div>
        {locations.length > 1 ? (
          <div className="flex items-center gap-sm">
            <MapPin className="w-4 h-4 text-secondary" />
            <select
              value={locationId}
              onChange={(e) => {
                setLocationId(e.target.value);
                // Release focus so a following scan can't change the location.
                e.currentTarget.blur();
              }}
              className="input"
              data-testid="quick-booking-location"
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.location_name}</option>
              ))}
            </select>
          </div>
        ) : location ? (
          <span className="flex items-center gap-sm text-sm text-secondary">
            <MapPin className="w-4 h-4" /> {location.location_name}
          </span>
        ) : null}
      </div>

      {locationsStatus !== 'loading' && locations.length === 0 && (
        <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg flex items-start gap-sm" data-testid="quick-booking-no-locations">
          <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0" />
          <p className="text-sm text-danger">
            {locationsStatus === 'error'
              ? t('quickBooking.locationsFailed', { error: locationsError || '' })
              : t('quickBooking.noLocations')}
          </p>
        </div>
      )}

      {created && (
        <div className="p-md bg-success/10 border-l-4 border-success rounded-lg flex flex-wrap items-center gap-md" data-testid="quick-booking-created">
          <Check className="w-5 h-5 text-success" />
          <span className="text-sm text-primary flex-1">{created.text}</span>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => navigate(`/bookings/${created.id}/edit`)}>
            {t('scan.openBooking')}
          </button>
        </div>
      )}

      {/* Customer */}
      <div className="card p-md sm:p-lg space-y-md">
        <div className="flex items-center gap-sm">
          <User className="w-5 h-5 text-blue" />
          <h2 className="font-semibold text-primary">{t('quickBooking.customer')}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-md relative">
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('quickBooking.firstName')} <span className="text-danger">*</span>
            </label>
            <input
              ref={firstNameRef}
              autoFocus
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); setCustomerSearchActive(true); }}
              className="input w-full"
              data-testid="quick-booking-firstname"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('quickBooking.lastName')} <span className="text-danger">*</span>
            </label>
            <input
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); setCustomerSearchActive(true); }}
              className="input w-full"
              data-testid="quick-booking-lastname"
            />
          </div>
          {customerResults.length > 0 && (
            <div
              className="border border-primary rounded-lg bg-primary shadow-sm overflow-hidden"
              style={{ gridColumn: '1 / -1' }}
              data-testid="quick-booking-customer-results"
            >
              {customerResults.map((c, i) => (
                <button
                  key={`${c.customer_email}-${i}`}
                  type="button"
                  onClick={() => selectCustomer(c)}
                  className="w-full text-left px-md py-sm text-sm hover:bg-secondary flex flex-wrap gap-x-md"
                >
                  <span className="font-medium text-primary">{c.customer_firstname} {c.customer_lastname}</span>
                  <span className="text-secondary">{c.customer_email}</span>
                  <span className="text-secondary">{c.customer_phone}</span>
                </button>
              ))}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('quickBooking.email')}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input w-full" />
            <p className="text-xs text-secondary mt-xs">{t('quickBooking.emailHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('quickBooking.phone')}</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="input w-full" />
          </div>
          {customFields.map((field) => (
            <div key={field.key}>
              {/* Labels may carry the HTML the booking frontend allows (<br>, <b>, links). */}
              {field.type === 'info' ? (
                // Display-only text; it has no input.
                <p
                  className="text-sm text-secondary"
                  dangerouslySetInnerHTML={{ __html: sanitizeCustomFieldHtml(field.label) }}
                />
              ) : (
                <label className="block text-sm font-medium text-primary mb-xs">
                  <span dangerouslySetInnerHTML={{ __html: sanitizeCustomFieldHtml(field.label) }} />
                  {field.required && <span className="text-danger"> *</span>}
                </label>
              )}
              {field.type === 'textarea' && (
                <textarea
                  value={customFieldValues[field.key] || ''}
                  maxLength={field.maxLength || 1000}
                  rows={field.rows || 4}
                  onChange={(e) => setCustomFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  className="input w-full"
                  placeholder={customFieldPlaceholder(field)}
                />
              )}
              {(field.type === 'text' || field.type === 'date' || field.type === 'datetime') && (
                <input
                  type={field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : 'text'}
                  value={customFieldValues[field.key] || ''}
                  maxLength={field.type === 'text' ? field.maxLength || 200 : undefined}
                  onChange={(e) => setCustomFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  className="input w-full"
                  placeholder={field.type === 'text' ? customFieldPlaceholder(field) : undefined}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Time */}
      <div className="card p-md sm:p-lg">
        {/* Inputs aligned on their bottom edge; theme.css has no .items-end. */}
        <div className="flex flex-wrap gap-md" style={{ alignItems: 'flex-end' }}>
          <div className="flex items-center gap-sm">
            <Clock className="w-5 h-5 text-blue" />
            <h2 className="font-semibold text-primary">{t('quickBooking.rentalTime')}</h2>
          </div>
          <div>
            <label className="block text-xs font-medium text-secondary mb-xs">{t('quickBooking.start')}</label>
            <div className="inline-flex rounded-lg border border-primary overflow-hidden" role="group">
              {(['now', 'later'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => chooseStartMode(mode)}
                  className={`px-md text-sm ${startMode === mode ? 'bg-blue text-white' : 'text-primary hover:bg-secondary'}`}
                  // Same height as the .input date fields next to it.
                  style={{ paddingTop: 'var(--spacing-md)', paddingBottom: 'var(--spacing-md)', lineHeight: '22px' }}
                  data-testid={`quick-booking-start-${mode}`}
                >
                  {mode === 'now' ? t('quickBooking.startNow') : t('quickBooking.startLater')}
                </button>
              ))}
            </div>
          </div>
          {/* Its own item in the row (bottom-aligned like the end field), so it
              can't wrap below the Now / Later buttons. */}
          {startMode === 'later' && (
            <div>
              <input
                type="datetime-local"
                value={startValue}
                onChange={(e) => setStartValue(e.target.value)}
                className={`input ${!startValid ? 'border-danger' : ''}`}
                aria-label={t('quickBooking.start')}
                data-testid="quick-booking-start"
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-secondary mb-xs">
              {t('quickBooking.end')} <span className="text-danger">*</span>
            </label>
            <input
              type="datetime-local"
              value={endValue}
              onChange={(e) => setEndValue(e.target.value)}
              className={`input ${!endValid ? 'border-danger' : ''}`}
              data-testid="quick-booking-end"
            />
          </div>
          {untilClosing && defaultEndIso && (
            <span className="text-xs text-secondary">
              {t('quickBooking.untilClosing', {
                time: startMode === 'later' ? formatDateTime(defaultEndIso) : formatTime(defaultEndIso),
              })}
            </span>
          )}
          {!defaultEndIso && startValid && (
            <span className="text-xs text-warning">
              {startMode === 'later' ? t('quickBooking.noClosingOnStartDay') : t('quickBooking.noClosingToday')}
            </span>
          )}
          {defaultEndIso && !untilClosing && (
            <button type="button" className="text-xs text-blue hover:underline" onClick={resetEndToDefault}>
              {t('quickBooking.resetToClosing')}
            </button>
          )}
        </div>
        {startMode === 'later' && (
          <p className="text-xs text-secondary mt-sm" data-testid="quick-booking-start-later-hint">
            {startValue ? t('quickBooking.startLaterHint') : t('quickBooking.noOpeningSoon')}
          </p>
        )}
      </div>

      {/* Items */}
      <div className="card p-md sm:p-lg space-y-md">
        <div className="flex flex-wrap items-center justify-between gap-sm">
          <div className="flex items-center gap-sm">
            <Package className="w-5 h-5 text-blue" />
            <h2 className="font-semibold text-primary">{t('quickBooking.items')}</h2>
            {(loadingAvailability || scanBusy) && <Loader2 className="w-4 h-4 animate-spin text-blue" />}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => setShowBundlePicker(true)}
            disabled={!endValid || bundleProducts.length === 0}
            data-testid="quick-booking-add-bundle"
          >
            <Layers className="w-4 h-4" />
            {t('quickBooking.addBundle')}
          </button>
        </div>

        <div className="p-md rounded-lg border border-dashed border-blue bg-blue/5 space-y-sm">
          <p className="text-sm text-blue">{t('quickBooking.scanPrompt')}</p>
          <ScanInput onSubmit={handleScan} testId="quick-booking-scan-input" />
        </div>

        {message && (
          <p className={`text-sm ${message.tone === 'error' ? 'text-danger' : 'text-success'}`} data-testid="quick-booking-message">
            {message.text}
          </p>
        )}
        {activeUnitHit && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => setReturnStart(activeUnitHit.start)}>
            {t('quickBooking.returnItFirst', { orderId: activeUnitHit.orderId })}
          </button>
        )}

        {lines.length === 0 ? (
          <p className="text-sm text-secondary">{t('quickBooking.noItemsYet')}</p>
        ) : (
          <ul className="space-y-xs" data-testid="quick-booking-lines">
            {lines.map((line) => (
              <li key={line.key} className="p-sm rounded-lg border border-primary">
                <div className="flex items-center gap-sm">
                  <button
                    type="button"
                    className="text-danger hover:text-danger/80"
                    title={t('quickBooking.remove')}
                    onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-primary flex-1">
                    {line.quantity > 1 ? `${line.quantity}× ` : ''}
                    {line.productName}
                    {line.size ? ` (${line.size})` : ''}
                    {line.unitLabel ? <span className="text-secondary"> · {line.unitLabel}</span> : null}
                  </span>
                  <span className="text-sm font-semibold text-primary">
                    {formatCurrency(line.pricePerUnit * line.quantity, currencyCode)}
                  </span>
                </div>
                {line.slots && (
                  <ul className="mt-xs pl-lg space-y-0.5">
                    {line.slots.map((slot, i) => (
                      <li key={i} className="text-xs flex items-center gap-xs">
                        {slot.inventoryType !== 'individual' || slot.itemIds.length >= slot.needed ? (
                          <Check className="w-3 h-3 text-success" />
                        ) : (
                          <ScanLine className="w-3 h-3 text-warning" />
                        )}
                        <span className="text-secondary">
                          {slot.needed > 1 ? `${slot.needed}× ` : ''}
                          {slot.product_name}
                          {slot.size ? ` (${slot.size})` : ''}
                          {slot.itemLabels.length > 0 ? `: ${slot.itemLabels.join(', ')}` : ''}
                          {slot.inventoryType === 'individual' && slot.itemIds.length < slot.needed && (
                            <span className="text-warning"> — {t('quickBooking.scanToFill')}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}

        {lines.length > 0 && (
          <div className="border-t border-primary pt-sm space-y-xs text-sm">
            {!showDiscount ? (
              <button type="button" className="text-xs text-blue hover:underline flex items-center gap-xs" onClick={() => setShowDiscount(true)}>
                <Ticket className="w-3 h-3" /> {t('quickBooking.addDiscount')}
              </button>
            ) : (
              <div className="flex flex-wrap items-center gap-sm">
                <input
                  value={discountCode}
                  onChange={(e) => { setDiscountCode(e.target.value); setDiscountValidation(null); }}
                  className="input h-8 text-sm"
                  placeholder={t('quickBooking.discountCode')}
                />
                <button type="button" className="btn btn-sm btn-secondary" onClick={handleApplyDiscount}>
                  {t('quickBooking.applyDiscount')}
                </button>
                {discountValidation?.valid && (
                  <span className="text-success text-xs">−{formatCurrency(discountAmount, currencyCode)}</span>
                )}
                {discountReason && <span className="text-danger text-xs">{discountReason}</span>}
              </div>
            )}
            {taxRate > 0 && !taxIncluded && (
              <div className="flex justify-between text-secondary">
                <span>{location?.tax_name || 'VAT'} ({(taxRate * 100).toFixed(0)}%)</span>
                <span>{formatCurrency(totals.totalTax, currencyCode)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-primary">
              <span>{t('newBooking.total')}</span>
              <span data-testid="quick-booking-total">{formatCurrency(totals.totalAmount, currencyCode)}</span>
            </div>
            {taxRate > 0 && taxIncluded && (
              <p className="text-xs text-secondary text-right">
                {t('newBooking.inclTax', {
                  taxName: location?.tax_name || 'VAT',
                  pct: (taxRate * 100).toFixed(0),
                  amount: formatCurrency(totals.totalTax, currencyCode),
                })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Payment */}
      <div className="card p-md sm:p-lg space-y-md">
        <div className="flex items-center gap-sm">
          <CreditCard className="w-5 h-5 text-blue" />
          <h2 className="font-semibold text-primary">{t('quickBooking.payment')}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-sm">
          {([
            { id: 'paid', label: t('newBooking.markAsPaid') },
            { id: 'link', label: t('newBooking.sendPaymentLink'), disabled: isFullyDiscounted || !email.trim(), hint: !isFullyDiscounted && !email.trim() ? t('quickBooking.linkNeedsEmail') : undefined },
            { id: 'later', label: t('newBooking.payLater'), disabled: isFullyDiscounted },
          ] as Array<{ id: 'paid' | 'link' | 'later'; label: string; disabled?: boolean; hint?: string }>).map((opt) => (
            <button
              key={opt.id}
              type="button"
              disabled={opt.disabled}
              onClick={() => setPaymentMethod(opt.id)}
              className={`p-md rounded-lg border text-left text-sm ${
                effectivePaymentMethod === opt.id ? 'border-blue bg-blue/10 ring-2 ring-blue' : 'border-primary hover:border-blue'
              } ${opt.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={`quick-booking-payment-${opt.id}`}
            >
              <span className="font-medium text-primary block">{opt.label}</span>
              {opt.hint && <span className="text-xs text-secondary">{opt.hint}</span>}
            </button>
          ))}
        </div>
        {isFullyDiscounted && (
          <p className="text-xs text-secondary" data-testid="quick-booking-fully-discounted-note">
            {t('newBooking.fullyDiscountedNote')}
          </p>
        )}
      </div>

      {formError && (
        <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg flex items-start gap-sm" data-testid="quick-booking-error">
          <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0" />
          <p className="text-sm text-danger">{formError}</p>
        </div>
      )}

      <div className="flex justify-end gap-md">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="quick-booking-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {t('quickBooking.submit')}
        </button>
      </div>

      {showBundlePicker && (
        <ScanModal title={t('quickBooking.addBundle')} onClose={() => setShowBundlePicker(false)} testId="quick-booking-bundle-picker">
          <div className="space-y-sm">
            {bundleProducts.map((b) => (
              <button
                key={b.product_type_id}
                type="button"
                onClick={() => addBundle(b)}
                className="w-full p-md rounded-lg border border-primary hover:border-blue hover:bg-blue/5 text-left"
              >
                <span className="font-medium text-primary">{b.product_name}</span>
                {isHiddenProduct(productFlags.get(b.product_type_id)) && (
                  <span className="ml-sm"><HiddenProductBadge /></span>
                )}
              </button>
            ))}
          </div>
        </ScanModal>
      )}

      {returnStart && (
        <ReturnScanModal
          start={returnStart}
          onClose={() => setReturnStart(null)}
          onSwitch={(next) => setReturnStart(next)}
          onEnded={() => {
            setActiveUnitHit(null);
            setMessage(null);
            loadAvailability();
          }}
        />
      )}
    </div>
  );
}
